import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Store, LogOut, Wallet, Loader2 } from 'lucide-react';
import { useProgramContext } from '../../context/ProgramContext';
import SelectContextFirst from '../../components/SelectContextFirst';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

interface HomeScreenProps {
  setAuth?: React.Dispatch<React.SetStateAction<boolean>>;
}

function HomeScreen({ setAuth }: HomeScreenProps) {
  const navigate = useNavigate();
  const {
    tenants,
    loading,
    selectedProgramId,
    isWalletAddressCollection,
  } = useProgramContext();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userDetails');
    setAuth?.(false);
    navigate('/');
  };

  const userName = (() => {
    try {
      const raw = localStorage.getItem('userDetails');
      if (!raw) return 'Agent';
      const u = JSON.parse(raw) as { name?: string; email?: string };
      return u.name || u.email?.split('@')[0] || 'Agent';
    } catch {
      return 'Agent';
    }
  })();

  const recentShops = tenants.slice(0, 5);
  const needsProgram = selectedProgramId == null;

  return (
    <div className="min-h-full bg-surface">
      <header className="fc-header">
        <h1 className="text-sm sm:text-base font-semibold text-slate-900 truncate">
          {getGreeting()}, {userName}
        </h1>
        <button
          onClick={handleLogout}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md hover:bg-gray-100 active:bg-gray-200 text-slate-600 flex-shrink-0 transition-colors duration-150"
          aria-label="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      <div className="p-3 pb-5 space-y-4 sm:space-y-5">
        {needsProgram ? (
          <SelectContextFirst />
        ) : (
          <>
            <section className="fc-card fc-card-hover p-3.5 sm:p-4">
              <h2 className="fc-section-title mb-4">
                <Wallet className="w-4 h-4 text-brand" />
                Today&apos;s Progress
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="fc-metric">
                  <div className="fc-metric-icon">
                    <Store className="w-5 h-5 text-brand" />
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-slate-900">{tenants.length}</p>
                    <p className="text-xs font-medium text-slate-500 tracking-tight">Shops</p>
                  </div>
                </div>
                <div className="fc-metric">
                  <div className="fc-metric-icon">
                    <Wallet className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-slate-900">
                      {tenants.reduce((n, t) => n + (t.vpaCount ?? 0), 0)}
                    </p>
                    <p className="text-xs font-medium text-slate-500 tracking-tight">
                      {isWalletAddressCollection ? 'Wallets' : 'VPAs'}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <button
              onClick={() => navigate('/app/collect')}
              className="fc-btn-primary w-full h-[48px]"
            >
              <PlusCircle className="w-5 h-5" />
              {isWalletAddressCollection ? 'Collect wallet address' : 'Collect New VPA'}
            </button>
            <button
              onClick={() => navigate('/app/shops')}
              className="w-full h-[46px] inline-flex items-center justify-center gap-2 rounded-[8px] border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              <Store className="w-4 h-4" />
              Browse All Shops
            </button>

            <section>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-600 mb-3">
                <Store className="w-4 h-4 text-slate-500" />
                Recent Shops
              </h2>
              {loading && tenants.length === 0 ? (
                <div className="fc-card-muted p-8 text-center text-gray-500 flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-brand" />
                  <span>Loading shops...</span>
                </div>
              ) : recentShops.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-500 flex flex-col items-center gap-3 transition-colors">
                  <Store className="w-10 h-10 text-slate-300" />
                  <span>No shops in this program</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentShops.map((shop) => (
                    <button
                      key={shop.tenantId}
                      onClick={() => navigate(`/app/shops/${shop.tenantId}`)}
                      className="fc-list-item"
                    >
                      <div className="flex items-center gap-3">
                        {shop.storeLogo && (
                          <img
                            src={shop.storeLogo}
                            alt=""
                            className="w-10 h-10 object-contain rounded border border-slate-100 bg-white"
                          />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{shop.tenantName}</p>
                          <p className="text-sm text-slate-500">{shop.tenantBrand}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">
                          {shop.vpaCount ?? 0}{' '}
                          {isWalletAddressCollection ? 'wallets' : 'VPAs'}
                        </span>
                        <Store className="w-4 h-4 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default HomeScreen;
